import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../../components/useBreadcrumbBack";
import { useAuth } from "../../../../providers/AuthProvider";
import { ApiError } from "../../../../services/apiClient";
import { listUsers } from "../../api/users";
import type { User } from "../../../../types/auth";
import { useApiQuery } from "../../../../hooks/useApiQuery";

import { PageShell } from "../../../shared/layouts/PageShell";
import { PageHeader } from "../../../shared/ui/layouts/PageHeader";

import { Avatar } from "../../../shared/ui/Avatar";
import {
  ArrowLeft,
  PencilSimple,
  Plus,
  SquaresFourIcon,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../../shared/ui/icons";
import { useToast } from "../../../../providers/ToastProvider";
import { storage } from "../../../shared/api/storage";
import { ADMIN_USERS_NOTICE_KEY, type AdminUsersNotice } from "./constants";

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const headerContentClasses = "max-w-3xl";
const headerTitleClasses = "text-4xl font-semibold text-slate-900 dark:text-slate-100";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const secondaryLinkClasses = "pm-btn pm-btn-tonal pm-btn-sm self-start";
const toolbarClasses =
  "flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:gap-4";
const searchInputClasses =
  "w-full flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder-slate-500";
const errorBannerClasses =
  "w-full rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300";
const tableWrapperClasses =
  "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900";
const placeholderClasses =
  "m-0 px-6 py-8 text-center text-slate-500 dark:text-slate-400";
const userCellClasses = "flex items-center gap-4";
const userNameClasses = "font-semibold text-slate-900 dark:text-slate-100";
const roleCellClasses = "capitalize font-semibold text-slate-600 dark:text-slate-400";
const actionsCellClasses = "text-right";

const primaryButtonClasses = "pm-btn pm-btn-primary";
const secondaryActionClasses = "pm-btn pm-btn-surface pm-btn-xs";

interface NavigationState {
  refreshed?: boolean;
}

export function AdminUsersPage() {
  const { t } = useTranslation();
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const queryFn = useCallback(
    (token: string) =>
      listUsers(token, {
        query: debouncedQuery
      }),
    [debouncedQuery]
  );

  const formatError = useCallback(
    (err: unknown) =>
      err instanceof ApiError ? err.message : t("admin.users.load_error"),
    [t]
  );

  const { data, isLoading, error, refresh } = useApiQuery<User[]>({
    queryFn,
    formatError
  });
  const users = useMemo(() => data ?? [], [data]);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("admin.title"), to: "/admin" },
      { label: t("admin.users.list_title") },
    ];
  }, [t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/admin" },
  });

  useEffect(() => {
    const navState = location.state as NavigationState | undefined;
    if (navState?.refreshed) {
      void refresh();
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, refresh]);

  useEffect(() => {
    const notice = storage.get<AdminUsersNotice>(ADMIN_USERS_NOTICE_KEY);
    if (notice?.message) {
      const tone = notice.type ?? "success";
      toast[tone](notice.message);
      storage.remove(ADMIN_USERS_NOTICE_KEY);
    }
  }, [toast]);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bDate - aDate;
    });
  }, [users]);

  if (!user || !accessToken) {
    return null;
  }

  if (user.system_role !== "admin") {
    return (
      <PageShell className={pageStackClasses} width="wide">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={t("common.access_denied.title")}
          subtitle={t("common.access_denied.message")}
          contentClassName={headerContentClasses}
          titleClassName={headerTitleClasses}
          actions={
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleBack}
            >
              {t("actions.back")}
            </button>
          }
        />
        <button
          type="button"
          className={secondaryLinkClasses}
          onClick={() => navigate("/dashboard")}
        >
          <SquaresFourIcon {...defaultIconProps} aria-hidden />
          {t("navigation.to_dashboard")}
        </button>
      </PageShell>
    );
  }

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleAddUser = () => {
    navigate("/admin/users/new");
  };

  const handleEditUser = (target: User) => {
    navigate(`/admin/users/${target.id}/edit`, {
      state: { user: target },
    });
  };

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />

      <PageHeader
        title={t("admin.users.list_title")}
        subtitle={t("admin.users.list_subtitle")}
        contentClassName={headerContentClasses}
        titleClassName={headerTitleClasses}
        actions={
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={handleBack}
          >
            <ArrowLeft {...defaultIconProps} aria-hidden />
            {t("actions.back")}
          </button>
        }
      />

      <div className={toolbarClasses}>
        <input
          type="search"
          placeholder={t("admin.users.search_placeholder")}
          value={searchInput}
          onChange={handleSearchChange}
          aria-label={t("admin.users.search_aria")}
          className={searchInputClasses}
        />
        <button
          type="button"
          className={primaryButtonClasses}
          onClick={handleAddUser}
        >
          <Plus {...defaultIconProps} aria-hidden />
          {t("admin.users.add_button")}
        </button>
      </div>

      {error && (
        <div className={errorBannerClasses} role="alert">
          {error}
        </div>
      )}

      <div className={tableWrapperClasses}>
        {isLoading ? (
          <p className={placeholderClasses}>{t("admin.users.loading")}</p>
        ) : sortedUsers.length === 0 ? (
          <p className={placeholderClasses}>
            {t("admin.users.empty_state")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600 dark:text-slate-400">
              <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-6 py-3">{t("common.table.name")}</th>
                  <th className="px-6 py-3">{t("common.table.email")}</th>
                  <th className="px-6 py-3">{t("admin.users.table.system_role")}</th>
                  <th className="px-6 py-3">{t("common.table.created")}</th>
                  <th className={`px-6 py-3 ${actionsCellClasses}`}>{t("common.table.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {sortedUsers.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                    <td className="px-6 py-4">
                      <div className={userCellClasses}>
                        <Avatar
                          name={item.name}
                          email={item.email}
                          photoUrl={item.profile_photo?.download_url ?? null}
                          fallbackUrl={item.profile_photo?.remote_url ?? null}
                          size={36}
                          className="shrink-0"
                        />
                        <div className="flex flex-col gap-[var(--pm-spacing-xxs)]">
                          <strong className={userNameClasses}>
                            {item.name?.trim() ||
                              item.email ||
                              t("common.table.empty_value")}
                          </strong>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">{item.email}</td>
                    <td className={`px-6 py-4 ${roleCellClasses}`}>{item.system_role}</td>
                    <td className="px-6 py-4">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleString()
                        : t("common.table.empty_value")}
                    </td>
                    <td className={`px-6 py-4 ${actionsCellClasses}`}>
                      <button
                        type="button"
                        className={secondaryActionClasses}
                        onClick={() => handleEditUser(item)}
                      >
                        <PencilSimple {...defaultIconProps} aria-hidden />
                        {t("actions.edit")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageShell>
  );
}
